import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/AliasesService';
import { Alias } from '../types';

export const useAlias = (aliasId?: string) => {
    const queryClient = useQueryClient();

    const aliasQuery = useQuery({
        queryKey: ['alias', aliasId],
        queryFn: () => {
            if (!aliasId) throw new Error('aliasId is required');
            return fetch(aliasId);
        },
        enabled: !!aliasId
    });

    const createMutation = useMutation({
        mutationFn: (values: Alias) => {
            const { created, updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (record: Alias) => {
            if (!aliasId) throw new Error('aliasId is required');
            return update(aliasId, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
            queryClient.invalidateQueries({ queryKey: ['alias', aliasId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!aliasId) throw new Error('aliasId is required');
            const record = aliasQuery.data;
            if (!record || !record.name) throw new Error('record with name is required');
            return deleteRecord(aliasId, record.name);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const fetchRecord = () => {
        aliasQuery.refetch();
    };

    const createRecord = (values: Alias) => {
        createMutation.mutate(values);
    };

    const updateRecord = (values: Alias) => {
        updateMutation.mutate(values);
    };

    const deleteRecordAction = () => {
        deleteMutation.mutate();
    };

    return {
        record: aliasQuery.data,
        loading: aliasQuery.isLoading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord: deleteRecordAction
    };
};
